import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiLink,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  HTTP_STATUSES,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiChevronLeft, mdiPlus } from "@mdi/js";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { AddRoleDrawer } from "./AddRoleDrawer/AddRoleDrawer";
import { useManageRoleViewModel } from "./useManageRoleViewModel";

export function ManageRole() {
  const { hasPermission } = usePermissions();

  const { navigate } = useRouter();
  const theme = useTheme();
  const {
    roles,
    openAddDrawer,
    selectedItem,
    isOpenDeleteModal,
    stateManager,
    getRoles,
    createRole,
    deleteRole,
    setSelectedItem,
    setOpenAddDrawer,
    setIsOpenDeleteModal,
  } = useManageRoleViewModel();
  const { classes } = useStyles();

  useEffect(() => {
    getRoles();
  }, []);

  const hasEditPermission = hasPermission(PERMISSIONS.ROLE.UPDATE);

  const roleFieldSet = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      groupable: false,
      flex: 1,
      renderCell: hasEditPermission
        ? (p) => (
            <MiLink
              key={p.row.id}
              title={p.value}
              to={`/settings/user-management/roles/${p.row.id}`}
            />
          )
        : null,
    },
    {
      field: "description",
      headerName: "Description",
      groupable: false,
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
  ];

  const onCreate = async (data) => {
    const response = await createRole(data);
    if (response.status === HTTP_STATUSES.OK) {
      setOpenAddDrawer(false);
      navigate(`/settings/user-management/roles/${response.data.id}`);
    }
  };

  const onDelete = async () => {
    const response = await deleteRole(selectedItem.id, {
      modifiedOn: selectedItem.modifiedOn,
      name: selectedItem.name,
    });
    setIsOpenDeleteModal(false);
    setSelectedItem(null);
    if (response.status === HTTP_STATUSES.OK) {
      getRoles();
    }
  };

  const onDeleteCancel = () => {
    setIsOpenDeleteModal(false);
    setSelectedItem(null);
  };

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.DELETE,
      onClick: () => {
        setSelectedItem(row);
        setIsOpenDeleteModal(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          size={1}
          color={theme.palette.red.main}
        />
      ),
    },
  ];

  return (
    <Box className={classes.root}>
      <div className={classes.contentContainer}>
        <MiDetailPageHeader
          title="Roles"
          titleIcon={mdiChevronLeft}
          loading={stateManager.isBusy()}
          actionComponent={
            hasPermission(PERMISSIONS.ROLE.CREATE) && (
              <MiButton
                title="Add Role"
                variant={BUTTON_VARIANTS.CONTAINED}
                color={BUTTON_COLORS.PRIMARY}
                startIcon={{
                  path: mdiPlus,
                  iconSize: 0.8,
                }}
                data-testid="add-role-btn"
                onClick={() => setOpenAddDrawer(true)}
                style={{
                  backgroundColor: theme.palette.primary.main,
                }}
              />
            )
          }
        />
        <Box className={classes.container}>
          <MiList
            columns={roleFieldSet}
            data={roles}
            paginationMode={PAGINATION.CLIENT}
            loading={stateManager.isBusy()}
            getRowActions={
              hasPermission(PERMISSIONS.ROLE.DELETE) && getRowActions
            }
            data-testid="users-table"
          />
        </Box>
        <AddRoleDrawer
          open={openAddDrawer}
          onClose={() => setOpenAddDrawer(false)}
          onSubmit={onCreate}
          loading={stateManager.isBusy()}
        />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          description={
            selectedItem?.usersCount > 0
              ? "This role is assigned to one or more users. Confirm deletion?"
              : `Are you sure you want to delete role: ${selectedItem?.name}?`
          }
          open={isOpenDeleteModal}
          handlePositive={onDelete}
          handleNegative={onDeleteCancel}
          className={classes.modal}
          textPositive={BUTTON_LABELS.DELETE}
        />
      </div>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "inherit",
    display: "flex",
  },
  container: {
    height: "inherit",
    overflowY: "auto",
    margin: 5,
    borderRadius: 10,
    padding: 5,
    backgroundColor: theme.palette.common.white,
  },
  contentContainer: { display: "flex", flexDirection: "column", width: "100%" },
  modal: {
    minWidth: 400,
  },
}));
